// Порт ref BadgeEditor.tsx:180-218 — поиск по каталогу + сетка 6 колонок с прокруткой.
// Отличия от рефа: (1) фильтр считаем по ДЕБАУНСНУТОМУ запросу — каталог ~500 иконок,
// и без этого каждое нажатие клавиши перерисовывает сотни svg; (2) у кнопки иконки
// есть aria-label (в рефе кнопка с одной svg внутри остаётся без доступного имени).
import { useMemo, useState } from 'react'
import { CircleSlash, Search } from 'lucide-react'
import { cn, Input } from '@teach/ui'
import { useDebouncedValue } from '@/lib/useDebouncedValue'
import { getLucideIcon, ICON_CATALOG } from '@/features/editor/lib/iconCatalog'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  value: string | null
  onPick: (name: string) => void
  /** Первая клетка «без иконки» (перечёркнутый кружок) — снять иконку. */
  onClear?: () => void
}

export const IconGrid = ({ value, onPick, onClear }: Props) => {
  const m = useMessages(messages)
  const [search, setSearch] = useState('')
  const query = useDebouncedValue(search).trim().toLowerCase()
  // Ищем и по имени, и по описанию: описания русские — именно их набирает автор.
  const icons = useMemo(
    () =>
      query
        ? ICON_CATALOG.filter(
            (i) =>
              i.name.toLowerCase().includes(query) || i.description.toLowerCase().includes(query),
          )
        : ICON_CATALOG,
    [query],
  )

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={m.iconSearchPlaceholder}
          className="h-8 pl-7 text-xs"
          // Как в рефе: вкладку открывают, чтобы искать — без фокуса нужен лишний клик.
          // Попап и так модальный, так что перехват фокуса тут ничего не ломает.
          autoFocus
        />
      </div>
      <div
        data-testid="icon-grid"
        className="grid max-h-48 grid-cols-6 gap-1 overflow-y-auto"
        role="group"
        aria-label={m.iconCatalog}
      >
        {onClear ? (
          <button
            type="button"
            aria-label={m.noIcon}
            onClick={onClear}
            className={cn(
              'flex h-7 w-7 items-center justify-center rounded transition-colors hover:bg-muted',
              value === null ? 'bg-primary/20' : null,
            )}
          >
            <CircleSlash className="h-4 w-4 text-muted-foreground" />
          </button>
        ) : null}
        {icons.map(({ name }) => {
          const Icon = getLucideIcon(name)
          return Icon ? (
            <button
              key={name}
              type="button"
              aria-label={name}
              onClick={() => onPick(name)}
              className={cn(
                'flex h-7 w-7 items-center justify-center rounded transition-colors hover:bg-muted',
                value === name ? 'bg-primary/20' : null,
              )}
            >
              <Icon className="h-4 w-4" />
            </button>
          ) : null
        })}
        {icons.length === 0 ? (
          <p className="col-span-6 py-3 text-center text-[11px] text-muted-foreground">
            {m.nothingFound}
          </p>
        ) : null}
      </div>
    </div>
  )
}
